import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { ILoyaltyCustomerExtended } from "pages/secure/facility/settings/loyaltyProgram/loyaltyTabs/LoyaltyCustomers";
import { IDiscountLines } from "redux/reducers/models/cart";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import { GetLoyaltyProgramRewards } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { IReward } from "redux/reducers/models/loyalty";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import ReactDOM from "react-dom";
import { LocaleCurrency } from "helpers/Locale";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import "./redeemPointsModal.scss";
import { ButtonNew as Button } from "components/buttonNew";
import { Badge } from "components/badge/Badge";

interface IProps {
    open: boolean;
    customer: ILoyaltyCustomerExtended & {
        points_available?: number;
    };
    onCancel: () => void;
    onOk: (rewards: Array<{ reward_id: number; points_to_use: number, cart_line_item_id: number }>) => void;
    currency: string;
    discountLines: Array<IDiscountLines>
    subtotal: number;
    handleRemove: (id: number) => void;
}

interface IState {
    reward: IReward;
}

export default function RedeemCashbackPointsModal(props: IProps) {
    const { open, onCancel, customer, discountLines, currency, subtotal, onOk, handleRemove } = props;
    const { t } = useTranslation();
    const [pointsToUse, setPointsToUse] = useState<string>("");
    const dispatch = useAppDispatch();
    const { getCurrencySymbol } = useCurrencyFacility();
    const currencySymbol = getCurrencySymbol(currency ?? "CAD");
    const [state, setState] = useState<IState>({
        reward: undefined,
    });
    const existingDiscount = discountLines?.find(
        discount =>
            discount?.loyalty_points_reward_id === state?.reward?.id,
    );

    const existingPoints = Number(
        existingDiscount?.loyalty_points_used ?? 0,
    );

    const isApplied = !!existingDiscount;

    const availablePoints = Math.max(0, customer?.points_balance ?? 0);
    const cashBackPoints = Number(state?.reward?.cash_back_points ?? 0);
    const cashBackValue = Number(state?.reward?.cash_back_value ?? 0);

    const existingDiscountAmount =
        cashBackPoints > 0
            ? (existingPoints / cashBackPoints) * cashBackValue
            : 0;

    // Restore the subtotal before the existing cashback discount.
    const subtotalBeforeCashback = subtotal + existingDiscountAmount;

    // Maximum whole redemption increments affordable by the subtotal.
    const pointsAllowedBySubtotal =
        cashBackPoints > 0 && cashBackValue > 0
            ? Math.floor(
                (subtotalBeforeCashback / cashBackValue) * cashBackPoints,
            )
            : 0;

    // Keep redemptions within both the customer's balance and subtotal.
    const maxRedeemablePoints = Math.min(
        Math.floor(availablePoints / cashBackPoints) * cashBackPoints,
        pointsAllowedBySubtotal,
    );

    const validPoints = Math.min(
        Math.max(0, Number(pointsToUse) || 0),
        maxRedeemablePoints,
    );

    const discountAmount =
        cashBackPoints > 0
            ? (validPoints / cashBackPoints) * cashBackValue
            : 0;

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void getRewards(source.token);
        } else {
            setState(prev => ({ ...prev, reward: undefined }));
        }
        return () => source.cancel();
    }, [open]);

    async function getRewards(cancelToken: CancelToken) {
        if (state?.reward !== undefined) {
            setState(prev => ({ ...prev, reward: undefined }));
        }
        const res = await GetLoyaltyProgramRewards(false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }
        const rewards: Array<IReward> = res?.data;
        const cashbackReward = rewards?.find(reward => reward?.cash_back);


       
        const usedDiscount = discountLines?.find(
            discount => discount?.loyalty_points_reward_id === cashbackReward?.id,
        );
        
        if (usedDiscount) {
            setPointsToUse(usedDiscount?.loyalty_points_used?.toString() ?? "");
        }

        ReactDOM.unstable_batchedUpdates(() => {
            setState(prev => ({ ...prev, reward: cashbackReward }));
            setPointsToUse(usedDiscount ? usedDiscount?.loyalty_points_used?.toString() ?? "" : "");
        });
    }

    return (
        <Portal isMounted={open}>
            <Sheet
                title={t("Redeem points")}
                supportText={t("Choose how many points to redeem for a cash-back discount on this order.")}
                open={open}
                onCancel={isApplied ? () => handleRemove(existingDiscount?.id) : () => onCancel()}
                onOk={() => onOk([{ reward_id: state?.reward?.id, points_to_use: Number(pointsToUse), cart_line_item_id: null }])}
                size="small"
                okText={t("Apply {{currencySymbol}}{{discountAmount}} discount", {discountAmount: discountAmount?.toFixed(2), currencySymbol: currencySymbol})}
                okDisabled={!state?.reward ||
                    validPoints <= 0 ||
                    validPoints > maxRedeemablePoints ||
                    cashBackPoints <= 0 ||
                    cashBackValue <= 0 ||
                    Number(pointsToUse) > maxRedeemablePoints
                }
                cancelButtonProps={isApplied ? {type: "text", style: {color: "#B42318"}} : undefined}
                cancelText={isApplied ? t("Remove reward") : t("Cancel")}
                secondaryText={t("Cancel")}
                onSecondary={isApplied ? () => onCancel() : undefined}
                backDropCancel={false}
            >
                {!state?.reward ? (
                    <div style={{ display: "flex", width: "100%", justifyContent: "center", marginTop: "1rem" }}>
                        <div className="flex justify-center items-center flex-col">
                            <FontAwesomeIcon icon={faSpinnerThird} spin />
                            <p className="text-sm text-gray-600">{t("Loading loyalty reward...")}</p>
                        </div>

                    </div>
                ) : (
                    <>
                        {isApplied ? (
                            <>
                            <div className="cashback-modal-applied-card">
                                <FontAwesomeIcon icon={faCircleInfo}/>
                                <p><span className="redeem-points-modal-rewards-title">{t("Currently redeemed:")}</span> <span className="redeem-points-modal-subtitle">{t("{{currentlyRedeemed}} points • {{currencySymbol}}{{discountApplied}}", {currentlyRedeemed: existingPoints.toLocaleString(), discountApplied: ((existingPoints / cashBackPoints) * cashBackValue)?.toFixed(2), currencySymbol: currencySymbol})}</span></p>
                            </div>
                            <br />
                            </>
                        ) : null}

                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Points to redeem")}
                                    value={pointsToUse}
                                    onChange={e => {
                                        const value = e.target.value;

                                        if (value === "") {
                                            setPointsToUse("");
                                            return;
                                        }

                                        const points = Number(value);

                                        if (!Number.isFinite(points)) {
                                            return;
                                        }

                                        if (points < 0) {
                                            setPointsToUse("");
                                        } else {
                                            setPointsToUse(
                                                value.replace(/[^0-9]/g, ""),
                                            );
                                        }
                                    }}
                                    placeholder={t("Enter points between 0 to {{maxPointsToUse}}", {maxPointsToUse: maxRedeemablePoints})}
                                    type="number"
                                    trailingButtons={[<Button key={1} type="secondary" size="xsmall" onClick={() => setPointsToUse(maxRedeemablePoints?.toString())} disabled={Number(pointsToUse) === maxRedeemablePoints}>{t("Use max points")}</Button>]}
                                    helpText={
                                        Number(pointsToUse) > maxRedeemablePoints ? t("Cannot redeem over {{maxPointsToUse}} available points.", {maxPointsToUse: maxRedeemablePoints}) :
                                        t("Available points: {{availablePoints}} • Maximum discount: {{currencySymbol}}{{maxDiscount}}", {
                                            availablePoints: customer?.points_balance, 
                                            maxDiscount: ((maxRedeemablePoints / cashBackPoints) * cashBackValue)?.toFixed(2),
                                            currencySymbol: currencySymbol,
                                        })
                                    }
                                    max={maxRedeemablePoints}
                                    error={Number(pointsToUse) > maxRedeemablePoints}
                                    min={0}
                                    step={1}
                                    tooltipText={`${cashBackPoints} ${cashBackPoints === 1
                                        ? t("pt")
                                        : t("pts")} = ${currencySymbol}${cashBackValue}`}
                                />
                            </FormLayout.Group>
                        </FormLayout>

                        <div className="cashback-modal-discount-container">
                            <div>
                                <p className="cashback-modal-discount-text">{isApplied ? t("New discount on this order") : t("Discount on this order")}</p>
                                <div className="flex items-center gap-2">
                                <LocaleCurrency
                                    style={{
                                        color: "#027A48",
                                        fontSize: "24px",
                                        fontWeight: 700,
                                    }}
                                    amount={discountAmount}
                                    currency={currency}
                                />
                                {isApplied ? <Badge size="small" type="warning" outline><LocaleCurrency
                                    amount={discountAmount - ((existingPoints / cashBackPoints) * cashBackValue)}
                                    currency={currency}
                                /></Badge> : null}
                                </div>
                                </div>
                                <div className="cashback-modal-discount-container-footer">
                                    <p><b>{t("{{remainingPoints}} pts", {remainingPoints: customer?.points_balance - Number(pointsToUse) >= 0 ? customer?.points_balance - Number(pointsToUse) : 0})}</b> {t("remaining after this order")}</p>
                                </div>
                        </div>
                    </>
                )}
            </Sheet>
        </Portal>
    );
}